import { processApiFieldErrors } from '../../utils/processApiFieldErrors'
// getErrorMessage lives in utils.js; TS imports it fine (allowJs: true).
import { getErrorMessage } from '../../utils/utils'
import { EVENT_BE_FIELD_MAP } from './beFieldMap'
import { EVENT_FIELD_TITLES } from './fieldTitles'

export interface ApplyEventApiErrorDeps {
  /** Formik `setErrors`. */
  setErrors: (errors: Record<string, string>) => void
  /** Formik `setTouched`. Web gates its error UI on `touched && error`, so
   *  API-set errors must touch their fields to render the red layout. */
  setTouched?: (touched: Record<string, boolean>) => void
  /** Summary toast (web: message.error). */
  notifyError: (message: string) => void
  /** Receives the errored form-field keys so the caller can scroll the first
   *  one into view. Fires only in the field-error branch. */
  onFieldErrors?: (fields: string[]) => void
}

/**
 * Translate a thrown event-save API error into Formik field errors. Inline
 * per-field messages are the only surface for field errors — no summary toast.
 * Falls back to a toast (backend `.message`, then getErrorMessage) when the
 * error carries no mapped `invalid_fields`.
 */
export const applyEventApiError = (
  err: unknown,
  deps: ApplyEventApiErrorDeps
): void => {
  const { setErrors, setTouched, notifyError, onFieldErrors } = deps

  // Guard: if err is not an object, fall back to toast immediately
  if (!err || typeof err !== 'object') {
    notifyError(getErrorMessage(err))
    return
  }

  const e = err as {
    invalid_fields?: Record<string, string | string[]>
    message?: string
  }

  const { formErrors, titles, hasFieldErrors } = processApiFieldErrors(
    e.invalid_fields,
    { fieldMap: EVENT_BE_FIELD_MAP, titleMap: EVENT_FIELD_TITLES }
  )

  if (!hasFieldErrors || titles.length === 0) {
    notifyError(e.message ?? getErrorMessage(err))
    return
  }

  setErrors(formErrors)
  setTouched?.(
    Object.keys(formErrors).reduce<Record<string, boolean>>((acc, key) => {
      acc[key] = true
      return acc
    }, {})
  )
  onFieldErrors?.(Object.keys(formErrors))
}
